import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
  Card,
  Radio,
  Checkbox,
  Input,
  Rate,
  Button,
  Typography,
  Space,
  Spin,
  Alert,
  DatePicker,
  message,
} from "antd";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;

const MY_EXIT_INTERVIEW_PATH = "/api/exitprocess/my-exit-interview/";

function toDayjs(value) {
  return value ? dayjs(value) : null;
}
function fromDayjs(value) {
  return value ? value.format("YYYY-MM-DD") : null;
}

export default function ExitInterviewForm() {
  const { authData, backUrl } = useContext(AuthContext);
  const [messageApi, contextHolder] = message.useMessage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [triggered, setTriggered] = useState(false);
  const [notTriggeredReason, setNotTriggeredReason] = useState("");
  const [responseMeta, setResponseMeta] = useState(null); // status, snapshots, etc.
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // question_id -> answer object
  const [signatureDate, setSignatureDate] = useState(null);

  function loadData() {
    setLoading(true);
    axios
      .get(`${backUrl}${MY_EXIT_INTERVIEW_PATH}`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      })
      .then((res) => {
        const d = res.data;
        setTriggered(!!d.triggered);
        if (!d.triggered) {
          setNotTriggeredReason(d.detail || "");
          return;
        }
        setQuestions(d.questions || []);
        setResponseMeta(d.response);
        setSignatureDate(d.response?.employee_signature_date ?? null);

        const answerMap = {};
        (d.response?.answers || []).forEach((a) => {
          answerMap[a.question.id] = {
            text_answer: a.text_answer ?? "",
            selected_options: a.selected_options ?? [],
            other_text: a.other_text ?? "",
            rating_value: a.rating_value ?? null,
            yes_no_value: a.yes_no_value ?? null,
          };
        });
        setAnswers(answerMap);
      })
      .catch((err) => {
        console.error("Error loading exit interview:", err);
        messageApi.error("Could not load the exit interview form.");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!authData?.access) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.access]);

  const isLocked = responseMeta?.status === "SUBMITTED" || responseMeta?.status === "REVIEWED";

  function patchAnswer(questionId, patch) {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || {}), ...patch },
    }));
  }

  function buildPayload(submit) {
    return {
      employee_signature_date: signatureDate,
      submit,
      answers: questions.map((q) => {
        const a = answers[q.id] || {};
        return {
          question_id: q.id,
          text_answer: a.text_answer || "",
          selected_options: a.selected_options || [],
          other_text: a.other_text || "",
          rating_value: a.rating_value ?? null,
          yes_no_value: a.yes_no_value ?? null,
        };
      }),
    };
  }

  async function handleSave(submit) {
    submit ? setSubmitting(true) : setSaving(true);
    try {
      const res = await axios.post(`${backUrl}${MY_EXIT_INTERVIEW_PATH}`, buildPayload(submit), {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setResponseMeta(res.data);
      messageApi.success(submit ? "Exit interview submitted. Thank you." : "Draft saved.");
    } catch (err) {
      console.error("Error saving exit interview:", err);
      messageApi.error(err.response?.data?.detail || "Could not save your answers.");
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  }

  const requiredMissing = useMemo(() => {
    return questions.some((q) => {
      if (!q.is_required) return false;
      const a = answers[q.id] || {};
      switch (q.question_type) {
        case "RATING":
          return !a.rating_value;
        case "YES_NO":
          return a.yes_no_value === null || a.yes_no_value === undefined;
        case "SINGLE_CHOICE":
        case "MULTI_CHOICE":
          return !a.selected_options || a.selected_options.length === 0;
        default:
          return !a.text_answer || !a.text_answer.trim();
      }
    });
  }, [questions, answers]);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!triggered) {
    return (
      <div style={{ padding: 24, maxWidth: 640, margin: "0 auto" }}>
        <Alert
          type="info"
          showIcon
          message="Exit interview not available"
          description={notTriggeredReason || "Your exit interview hasn't been triggered by HR yet. Please check back later."}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      {contextHolder}
      <Card>
        <Title level={4} style={{ marginBottom: 0 }}>
          Exit Interview Questionnaire
        </Title>
        <Text type="secondary">
          Please answer as honestly as possible - your feedback helps us improve.
        </Text>

        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <div>
            <Text strong>Name: </Text>
            <Text>{responseMeta?.employee_name_snapshot || "—"}</Text>
          </div>
          <div>
            <Text strong>HOD: </Text>
            <Text>{responseMeta?.hod_name_snapshot || "—"}</Text>
          </div>
          <div>
            <Text strong>Emp No: </Text>
            <Text>{responseMeta?.emp_no_snapshot || "—"}</Text>
          </div>
          <div>
            <Text strong>Position: </Text>
            <Text>{responseMeta?.current_position_snapshot || "—"}</Text>
          </div>
        </div>

        {isLocked && (
          <Alert
            style={{ marginBottom: 24 }}
            type="success"
            showIcon
            message="This exit interview has already been submitted."
          />
        )}

        <Space direction="vertical" size={28} style={{ width: "100%" }}>
          {questions.map((q, idx) => (
            <QuestionBlock
              key={q.id}
              index={idx + 1}
              question={q}
              answer={answers[q.id] || {}}
              disabled={isLocked}
              onChange={(patch) => patchAnswer(q.id, patch)}
            />
          ))}
        </Space>

        <div style={{ marginTop: 32 }}>
          <Text strong>Employee Signature / Date</Text>
          <div style={{ marginTop: 8 }}>
            <DatePicker
              disabled={isLocked}
              value={toDayjs(signatureDate)}
              onChange={(v) => setSignatureDate(fromDayjs(v))}
              format="DD MMM YYYY"
            />
          </div>
        </div>

        {!isLocked && (
          <Space style={{ marginTop: 32 }}>
            <Button loading={saving} onClick={() => handleSave(false)}>
              Save draft
            </Button>
            <Button
              type="primary"
              loading={submitting}
              disabled={requiredMissing}
              onClick={() => handleSave(true)}
            >
              Submit
            </Button>
          </Space>
        )}
        {!isLocked && requiredMissing && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Please answer all required questions before submitting.</Text>
          </div>
        )}
      </Card>
    </div>
  );
}

function QuestionBlock({ index, question, answer, disabled, onChange }) {
  const { question_text, question_type, options, allow_other, help_text, is_required } = question;

  return (
    <div>
      <Text strong>
        {index}. {question_text}
        {is_required && <span style={{ color: "#ff4d4f" }}> *</span>}
      </Text>
      {help_text && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {help_text}
          </Text>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        {question_type === "SINGLE_CHOICE" && (
          <Radio.Group
            disabled={disabled}
            value={answer.selected_options?.[0] || null}
            onChange={(e) => onChange({ selected_options: [e.target.value] })}
          >
            <Space direction="vertical">
              {(options || []).map((opt) => (
                <Radio key={opt} value={opt}>
                  {opt}
                </Radio>
              ))}
              {allow_other && (
                <Radio value="Other">
                  Other
                  {answer.selected_options?.[0] === "Other" && (
                    <Input
                      style={{ width: 240, marginLeft: 8 }}
                      disabled={disabled}
                      placeholder="Please specify"
                      value={answer.other_text || ""}
                      onChange={(e) => onChange({ other_text: e.target.value })}
                    />
                  )}
                </Radio>
              )}
            </Space>
          </Radio.Group>
        )}

        {question_type === "MULTI_CHOICE" && (
          <div>
            <Checkbox.Group
              disabled={disabled}
              value={answer.selected_options || []}
              onChange={(vals) => onChange({ selected_options: vals })}
            >
              <Space direction="vertical">
                {(options || []).map((opt) => (
                  <Checkbox key={opt} value={opt}>
                    {opt}
                  </Checkbox>
                ))}
                {allow_other && <Checkbox value="Other">Other</Checkbox>}
              </Space>
            </Checkbox.Group>
            {allow_other && answer.selected_options?.includes("Other") && (
              <Input
                style={{ width: 280, marginTop: 8 }}
                disabled={disabled}
                placeholder="Please specify"
                value={answer.other_text || ""}
                onChange={(e) => onChange({ other_text: e.target.value })}
              />
            )}
          </div>
        )}

        {question_type === "RATING" && (
          <Rate
            disabled={disabled}
            value={answer.rating_value || 0}
            onChange={(v) => onChange({ rating_value: v })}
          />
        )}

        {question_type === "YES_NO" && (
          <Radio.Group
            disabled={disabled}
            value={answer.yes_no_value}
            onChange={(e) => onChange({ yes_no_value: e.target.value })}
          >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        )}

        {question_type === "TEXT" && (
          <TextArea
            rows={3}
            disabled={disabled}
            value={answer.text_answer || ""}
            onChange={(e) => onChange({ text_answer: e.target.value })}
            placeholder="Type your answer..."
          />
        )}
      </div>
    </div>
  );
}